/**
 * How a flag's value is decided for a given user.
 *
 * - `user`: evaluated by posthog-node at SSR time with the user's distinctId.
 *   Requires the route's `cacheMode: 'private'` (per-user HTML, not shared at
 *   the CDN). See docs/projects/active/a-b-test-public-doc.md §3.
 * - `edge`: deterministically hashed from `lis_bucket` + flag key in a
 *   CloudFront Function (or the SSR-lambda fallback). Compatible with
 *   shared-CDN cache via the `x-variants` cache-key dimension.
 * - `local`: dev-only toggle. Overridden via the TanStack DevTools panel or
 *   the `analytics.flags.set(...)` console API. Not driven by PostHog.
 */
export type BucketingMode = 'user' | 'edge' | 'local';

export type BucketingConfig<V extends readonly string[] = readonly string[]> =
  | { mode: 'user' }
  | { mode: 'local' }
  | {
      mode: 'edge';
      /** Fraction of traffic enrolled in the experiment ([0, 1]). */
      enrollment: number;
      /** Weights per variant; must sum to 1.0. */
      split: Record<V[number], number>;
      /**
       * Variant served to requests that carry no `lis_bucket` cookie and to
       * known crawlers. No exposure event fires for them — they are outside
       * the experiment, not a third arm.
       *
       * Distinct from `default`: `default` is what a non-enrolled or
       * not-yet-loaded read returns, while this is a deliberate editorial
       * choice about what an unbucketable viewer sees. For price experiments
       * that means the top-priced arm, so advertised-below-charged stays
       * impossible when bot detection misses.
       *
       * Edge-visible on purpose — the CloudFront function is what applies it.
       * See docs/projects/active/edge-experiments-account-pinned-bucketing.md
       * §3.5.
       */
      fallbackVariant: V[number];
    };

/**
 * The only part of an experiment's definition the CloudFront Function reads.
 *
 * `handler-request.ts` uses exactly these four fields, and the viewer-request
 * bundle is the one place in the repo where an unread object property still
 * costs real money — rolldown cannot tree-shake properties, so `variants`,
 * `default` and `bucketing.mode` shipped to the edge purely to be ignored.
 * Measured at 185 bytes across three experiments, which is the difference
 * between the six-arm price test fitting the budget and not fitting it.
 *
 * So `edge-bucketing.ts` holds these fields and the edge imports THAT;
 * `EdgeClientFlags` composes the full `FeatureFlagDef` from it for everyone
 * else. Same split, same reason, as `ExperimentPolicy` below.
 */
export type EdgeBucketingDef = {
  /** PostHog flag key. Hashed with the bucket id — renaming re-rolls everyone. */
  readonly key: string;
  /** Fraction of traffic enrolled ([0, 1]). */
  readonly enrollment: number;
  /** Weights per variant; must sum to 1.0. */
  readonly split: Readonly<Record<string, number>>;
  /** See {@link BucketingConfig}'s `fallbackVariant`. */
  readonly fallbackVariant: string;
};

/**
 * What identity keeps an edge experiment's variant stable.
 *
 * - `none`: Mode A. The `lis_bucket` cookie is the whole identity — a new
 *   device, a cleared cookie, or signing up re-rolls the assignment.
 * - `at-signup`: Mode C. The cookie-derived variant is frozen onto the account
 *   at signup and never recomputed, so a split edit or a lost cookie cannot
 *   move it.
 *
 * See docs/projects/active/edge-experiments-account-pinned-bucketing.md §3.
 */
export type PinningMode = 'none' | 'at-signup';

/**
 * The half of an edge experiment's definition that no CloudFront Function
 * reads.
 *
 * Held in `edge-experiment-policies.ts` rather than on the flag def because
 * `EdgeClientFlags` ships whole into the ≤4 KB edge bundle (rolldown cannot
 * tree-shake object properties) and these two fields do not fit: measured,
 * carrying them on the flag defs puts the viewer-request bundle at 3,679
 * bytes against the 3,674-byte budget asserted in `packages/infra/src/web/edge.ts`.
 * `fallbackVariant` stays on `BucketingConfig` because the edge does apply it.
 */
export type ExperimentPolicy = {
  pinning: PinningMode;
  /**
   * Declarative name of the single event at which this experiment's exposure
   * fires. Marketing pageviews must never emit it, or SEO traffic floods the
   * denominator. Consumers wire this up in a later milestone; today it is a
   * label the definition is required to state.
   */
  exposureAt: string;
};

export type FeatureFlagDef<V extends readonly string[] = readonly string[]> = {
  /** PostHog flag key (e.g., 'backend-version') */
  key: string;
  label?: string;
  /** All possible variant values */
  variants: V;
  variantLabels?: Partial<Record<V[number], string>>;
  /** Default value when flags haven't loaded or the flag is missing */
  default: V[number];
  /** Where this flag's value is decided. See {@link BucketingMode}. */
  bucketing: BucketingConfig<V>;
};

/** Edge-bucketed flag definition (narrowed `bucketing.mode`). */
export type EdgeFeatureFlagDef = FeatureFlagDef & {
  bucketing: Extract<BucketingConfig, { mode: 'edge' }>;
};

/**
 * Narrow a flag def to edge-bucketed. Returns undefined for missing defs and
 * non-edge flags. Safe when `EdgeClientFlags` is empty (`EdgeBucketedFlagKey`
 * collapses to `never` and lookup bodies still need a runtime/typed escape).
 */
export function asEdgeFlagDef(
  def: FeatureFlagDef | undefined,
): EdgeFeatureFlagDef | undefined {
  if (!def || def.bucketing.mode !== 'edge') return undefined;
  return def as EdgeFeatureFlagDef;
}

/** Extract the variant union type from a flag definition */
export type FlagVariant<F extends FeatureFlagDef> = F['variants'][number];

/** Extract variant types from a flags record */
export type FlagVariants<T extends Record<string, FeatureFlagDef>> = {
  [K in keyof T]: FlagVariant<T[K]>;
};
